// Contact / Booking Page - Updated with client's brand messaging
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Check } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta } from "../components/shared/SEOHelper";

// Contact Page Schema
const ContactSchema = () => {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Annelie Lily",
      "description": "Get in touch with Annelie Lily for yoga classes, holistic treatments, women's circles and retreats in Frome, Somerset.",
      "url": "https://annelielily.com/contact",
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Annelie Lily - Yoga & Holistic Healing",
        "telephone": "+44 7540 995021",
        "email": "seventhsensehealing@outlook.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Little Keyford Lane",
          "addressLocality": "Frome",
          "addressRegion": "Somerset",
          "postalCode": "BA11 5BA",
          "addressCountry": "GB"
        }
      }
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'contact-schema';
    document.head.appendChild(script);
    
    return () => {
      const el = document.getElementById('contact-schema');
      if (el) el.remove();
    };
  }, []);
  
  return null;
};

const ContactPage = () => {
  usePageMeta({
    title: "Contact Annelie | Yoga, Treatments, Circles & Retreats |",
    description: "Get in touch with Annelie Lily for yoga classes, holistic treatments, women's circles and retreats in Frome, Somerset. Call 07540 995021 or email to book.",
    canonical: "https://annelielily.com/contact"
  });

  const [formData, setFormData] = useState({ 
    name: "", 
    email: "", 
    phone: "",
    interest: "yoga-class",
    message: "",
    consent: false
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) {
      setStatus({ type: "error", message: "Please tick the consent box to continue." });
      return;
    }
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const formSubmitData = new FormData();
      formSubmitData.append("name", formData.name);
      formSubmitData.append("email", formData.email);
      formSubmitData.append("phone", formData.phone || "Not provided");
      formSubmitData.append("Interested in", formData.interest);
      formSubmitData.append("message", formData.message);
      formSubmitData.append("_subject", `Website Enquiry from ${formData.name}`);
      formSubmitData.append("_captcha", "false");
      formSubmitData.append("_template", "table");

      await fetch("https://formsubmit.co/ajax/seventhsensehealing@outlook.com", {
        method: "POST",
        body: formSubmitData
      });
      
      setStatus({ type: "success", message: "Thanks for your message. I'll get back to you within 24-48 hours." });
      setFormData({ name: "", email: "", phone: "", interest: "yoga-class", message: "", consent: false });
    } catch (error) {
      setStatus({ type: "error", message: "Something went wrong. Please try emailing or calling directly." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <ContactSchema />
      <Navigation />
      <PageBreadcrumb currentPage="Contact" currentSlug="contact" />
      
      {/* Hero */}
      <section className="pt-8 pb-12 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Get in Touch
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Contact Annelie
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-8">
              I'd love to hear from you. Whether you want to book a class, a treatment, join a circle or retreat, 
              or just have a question — send me a message and I'll get back to you.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:07540995021"
                className="btn-secondary"
                data-testid="contact-hero-call-btn"
              >
                Call 07540 995021
              </a>
            </div>

            <div className="mt-8 bg-white/70 rounded-2xl border border-gold/10 p-5 max-w-xl mx-auto text-left" data-testid="contact-booking-links">
              <p className="font-heading text-lg text-neutral-800 mb-3 text-center">Book Now</p>
              <div className="grid sm:grid-cols-2 gap-2">
                <Link to="/yoga-classes#book" className="block text-neutral-700 hover:text-gold transition-colors text-sm" data-testid="contact-book-yoga-link">
                  → Book a Yoga Class
                </Link>
                <Link to="/treatments#book" className="block text-neutral-700 hover:text-gold transition-colors text-sm" data-testid="contact-book-treatments-link">
                  → Book a Treatment or Healing Session
                </Link>
                <Link to="/womens-circles#book" className="block text-neutral-700 hover:text-gold transition-colors text-sm" data-testid="contact-book-circle-link">
                  → Book a Women's Circle
                </Link>
                <Link to="/retreats#book" className="block text-neutral-700 hover:text-gold transition-colors text-sm" data-testid="contact-book-retreats-link">
                  → Book a Retreat
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Warm portrait */}
      <section className="px-6 md:px-12 bg-cream pt-10 pb-4" data-testid="contact-portrait">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="aspect-[4/3] md:aspect-[16/10] rounded-2xl overflow-hidden shadow-xl border border-gold/10">
              <img
                src="https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/vjkiskbu_1B29DE7B-D45D-436B-961A-43BE56B9E6D6.jpg"
                alt="Annelie Lily smiling warmly at golden hour with a mala necklace, palm tree and Mediterranean villa in the background"
                className="w-full h-full object-cover"
                width={1600}
                height={1000}
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="hidden md:block absolute -top-3 -left-3 w-6 h-6 bg-gold/30 rounded-full" />
            <div className="hidden md:block absolute -bottom-4 -right-4 w-10 h-10 bg-gold/20 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section id="contact-form" className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl text-neutral-800 mb-6">Contact Details</h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-neutral-800 font-medium mb-1">Phone</p>
                    <a href="tel:07540995021" className="text-neutral-600 hover:text-gold transition-colors">
                      07540 995021
                    </a>
                    <p className="text-neutral-500 text-sm mt-1">Call or text — I'll respond when I'm not teaching</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-neutral-800 font-medium mb-1">Email</p>
                    <a href="mailto:seventhsensehealing@outlook.com" className="text-neutral-600 hover:text-gold transition-colors break-all">
                      seventhsensehealing@outlook.com
                    </a>
                    <p className="text-neutral-500 text-sm mt-1">Usually reply within 24-48 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-neutral-800 font-medium mb-1">Location</p>
                    <p className="text-neutral-600">
                      Little Keyford Lane<br />
                      Frome, Somerset<br />
                      BA11 5BA
                    </p>
                    <p className="text-neutral-500 text-sm mt-1">Parking available nearby</p>
                  </div>
                </div>
              </div>

              {/* Areas */}
              <div className="mt-8">
                <p className="text-neutral-500 text-sm mb-2">Coming from nearby?</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Bath", link: "/areas/yoga-bath" },
                    { name: "Trowbridge", link: "/areas/yoga-trowbridge" },
                    { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet" },
                  ].map((area) => (
                    <Link
                      key={area.name}
                      to={area.link}
                      className="text-xs bg-gold/10 text-gold px-3 py-1.5 rounded-full hover:bg-gold/20 transition-colors"
                    >
                      {area.name}
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="font-heading text-2xl text-neutral-800 mb-6">Send a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-neutral-800 mb-1">
                    Your name *
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-field"
                    required
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-800 mb-1">
                    Email address *
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field"
                    required
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-neutral-800 mb-1">
                    Phone (optional)
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-field"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <label htmlFor="interest" className="block text-sm font-medium text-neutral-800 mb-1">
                    What are you interested in?
                  </label>
                  <select
                    id="interest"
                    value={formData.interest}
                    onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                    className="input-field"
                    data-testid="contact-interest-input"
                  >
                    <option value="yoga-class">Yoga classes</option>
                    <option value="private-session">Private sessions</option>
                    <option value="treatment">Holistic treatment</option>
                    <option value="womens-circle">Women's circle</option>
                    <option value="retreat">Day retreat</option>
                    <option value="general">General enquiry</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-neutral-800 mb-1">
                    Your message *
                  </label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    className="input-field resize-none"
                    required
                    placeholder="Tell me a bit about yourself and what you're looking for..."
                    data-testid="contact-message-input"
                  />
                </div>

                <div className="flex items-start gap-3">
                  <input
                    id="consent"
                    type="checkbox"
                    checked={formData.consent}
                    onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                    className="mt-1 w-4 h-4 text-gold border-neutral-300 rounded focus:ring-gold"
                    data-testid="contact-consent-input"
                  />
                  <label htmlFor="consent" className="text-sm text-neutral-600">
                    I'm happy for Annelie Lily to contact me about my enquiry. *
                  </label>
                </div>

                {status.message && (
                  <div className={`p-4 rounded-lg ${status.type === "success" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`} data-testid="form-status">
                    {status.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl text-neutral-800 mb-8 text-center">Common Questions</h2>
            
            <div className="space-y-4">
              <div className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">How do I book a class?</h3>
                <p className="text-neutral-600 text-sm">
                  Just send me a message using the form above, or call/text. I'll let you know what's available 
                  and help you find a class that suits your experience level and schedule.
                </p>
              </div>

              <div className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">How much do classes cost?</h3>
                <p className="text-neutral-600 text-sm">
                  Prices vary depending on the type of session. Drop-in classes, block bookings, and private 
                  sessions all have different rates. Get in touch and I'll send you current pricing.
                </p>
              </div>

              <div className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">I've never done yoga before — is that okay?</h3>
                <p className="text-neutral-600 text-sm">
                  Absolutely. Many of my students started as complete beginners. Classes are small enough that 
                  I can give you individual attention, and I always offer easier options for every pose.
                </p>
              </div>

              <div className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">What if I have an injury or health condition?</h3>
                <p className="text-neutral-600 text-sm">
                  Let me know when you get in touch. Many conditions can be worked with safely — I just need 
                  to know what's going on so I can adapt things appropriately. If you're unsure, we can always 
                  start with a private session.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
